import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dashboardResearchPath } from '../constants/dashboardRoutes';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { ArrowUpRight, ChevronDown, ChevronUp, Search } from 'react-bootstrap-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useCompanyFundamentals } from '../hooks/useCompanyFundamentals';
import { Header } from '../components/Header';
import MiniStockChart from '../components/shared/MiniStockChart';

// ─── Formatters ──────────────────────────────────────────────────────────────

function fmtLargeNum(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toFixed(2)}`;
}

function fmtPct(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

/** AV PercentInsiders / PercentInstitutions are already percent values (e.g. 54.3 = 54.3%). */
function fmtPercentDirect(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function fmtGrowthYoY(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(1)}%`;
}

function fmtNum(val, prefix = '', suffix = '') {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `${prefix}${n.toFixed(2)}${suffix}`;
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return val;
  }
}

function fmtShares(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  return n.toLocaleString();
}

// ─── Skeleton loader ─────────────────────────────────────────────────────────

const Skeleton = ({ width = '100%', height = 20, radius = 6, colors }) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      background: `linear-gradient(90deg, ${colors.bgCard} 25%, ${colors.bgSecondary} 50%, ${colors.bgCard} 75%)`,
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }}
  />
);

// ─── Metric definitions ──────────────────────────────────────────────────────

const METRIC_DEFINITIONS = {
  'Market Cap': 'Total market value of a company\'s outstanding shares. Calculated as share price × shares outstanding. Larger = bigger company.',
  'Revenue TTM': 'Total sales revenue earned over the trailing twelve months. Shows how much money the company brings in.',
  'Net Margin': 'Percentage of revenue kept as profit after all expenses. Higher = more efficient at turning sales into profit.',
  'EPS (TTM)': 'Earnings Per Share — net profit divided by shares outstanding. Shows how much the company earns per share you own.',
  'Gross Margin': 'Revenue minus cost of goods sold, as a % of revenue. Shows profitability before overhead costs.',
  'Op Margin': 'Operating profit as a % of revenue. Shows how efficiently the company runs its core business.',
  'ROE': 'Return on Equity — net income divided by shareholder equity. Measures how effectively management uses investor capital.',
  'ROA': 'Return on Assets — net income divided by total assets. Shows how efficiently the company uses its assets to generate profit.',
  'EBITDA': 'Earnings before interest, taxes, depreciation, and amortization. A proxy for operating cash flow.',
  'P/E': 'Price-to-Earnings ratio — stock price divided by EPS. Shows how much investors pay per $1 of earnings. Lower can mean cheaper.',
  'Fwd P/E': 'Forward P/E uses next year\'s estimated earnings. Gives a more future-looking valuation than trailing P/E.',
  'P/B': 'Price-to-Book ratio — market cap divided by book value. Below 1 may indicate undervaluation.',
  'EV/EBITDA': 'Enterprise Value divided by EBITDA. A valuation multiple used to compare companies regardless of capital structure.',
  'P/S': 'Price-to-Sales ratio — market cap divided by revenue. Useful for companies with negative earnings.',
  'Beta': 'Measures volatility relative to the market. Beta > 1 = more volatile than S&P 500. Beta < 1 = less volatile.',
  'Div Yield': 'Annual dividend per share divided by stock price. Shows income return as a % of your investment.',
  'Insider %': 'Percentage of shares held by insiders (executives, directors, and other affiliates).',
  'Institutional %': 'Percentage of shares held by institutional investors (funds, pensions, banks, etc.).',
  'Rev Growth YoY': 'Quarterly revenue growth year-over-year from the latest reported quarter.',
  'EPS Growth YoY': 'Quarterly earnings growth year-over-year from the latest reported quarter.',
};

// ─── Viewport-aware tooltip ───────────────────────────────────────────────────

const TOOLTIP_W = 220;

// Module-level reference so only one tooltip can be visible at a time.
let activeHideFn = null;

const MetricTooltip = ({ text, colors, children }) => {
  const triggerRef = useRef(null);
  const [tipPos, setTipPos] = useState(null);
  const hideRef = useRef(null);

  const hide = useCallback(() => {
    setTipPos(null);
    if (activeHideFn === hideRef.current) activeHideFn = null;
  }, []);
  hideRef.current = hide;

  const show = useCallback(() => {
    if (!text || !triggerRef.current) return;
    // Close any currently open tooltip from another instance.
    if (activeHideFn && activeHideFn !== hideRef.current) activeHideFn();
    const rect = triggerRef.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(
        rect.left + rect.width / 2 - TOOLTIP_W / 2,
        window.innerWidth - TOOLTIP_W - 8,
      ),
    );
    setTipPos({ left, top: rect.top - 6 });
    activeHideFn = hideRef.current;
  }, [text]);

  // Dismiss when the user taps/clicks anywhere outside the trigger.
  useEffect(() => {
    if (!tipPos) return;
    const dismiss = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) hide();
    };
    document.addEventListener('touchstart', dismiss, { passive: true });
    document.addEventListener('mousedown', dismiss);
    return () => {
      document.removeEventListener('touchstart', dismiss);
      document.removeEventListener('mousedown', dismiss);
    };
  }, [tipPos, hide]);

  return (
    <div
      ref={triggerRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onTouchStart={() => (tipPos ? hide() : show())}
      style={{ position: 'relative' }}
    >
      {children}
      {tipPos && (
        <div
          style={{
            position: 'fixed',
            left: tipPos.left,
            top: tipPos.top,
            transform: 'translateY(-100%)',
            width: TOOLTIP_W,
            background: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
            color: colors.text,
            lineHeight: 1.6,
            zIndex: 9999,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
            whiteSpace: 'normal',
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, accentColor, colors }) => {
  const def = METRIC_DEFINITIONS[label];
  const card = (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        cursor: def ? 'default' : undefined,
      }}
    >
      <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: 24, fontWeight: 700, color: accentColor || colors.text, lineHeight: 1.1, marginTop: 2 }}>
        {value}
      </span>
      {sub && (
        <span style={{ fontSize: 12, color: colors.textMuted }}>
          {sub}
        </span>
      )}
    </div>
  );
  return def
    ? <MetricTooltip text={def} colors={colors}>{card}</MetricTooltip>
    : card;
};

// ─── Stat Pill ───────────────────────────────────────────────────────────────

const StatPill = ({ label, value, colors }) => {
  const def = METRIC_DEFINITIONS[label];
  const pill = (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: '8px 14px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        minWidth: 90,
        cursor: def ? 'default' : undefined,
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>{value}</span>
      <span style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
    </div>
  );
  return def
    ? <MetricTooltip text={def} colors={colors}>{pill}</MetricTooltip>
    : pill;
};

// ─── Custom Recharts Tooltip ──────────────────────────────────────────────────

function fmtEps(val) {
  if (val == null || Number.isNaN(val)) return '—';
  return `$${Number(val).toFixed(2)}`;
}

function fmtSurprisePct(val) {
  if (val == null || Number.isNaN(val)) return null;
  const sign = val > 0 ? '+' : '';
  return `${sign}${Number(val).toFixed(1)}%`;
}

const EpsTooltip = ({ active, payload, label, colors, isQuarterly }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const surprisePct = fmtSurprisePct(d?.surprisePercentage);
  const beat =
    d?.surprise == null ? null : d.surprise > 0 ? 'Beat' : d.surprise < 0 ? 'Miss' : 'Inline';
  const beatColor =
    beat === 'Beat' ? colors.success : beat === 'Miss' ? colors.danger : colors.textMuted;

  return (
    <div
      style={{
        background: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 12,
        color: colors.text,
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        minWidth: 160,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6, color: colors.textMuted }}>{label}</div>
      {isQuarterly ? (
        <>
          <div>
            Actual:{' '}
            <strong style={{ color: d?.eps >= 0 ? colors.success : colors.danger }}>
              {fmtEps(d?.eps)}
            </strong>
          </div>
          <div style={{ marginTop: 4 }}>
            Estimate:{' '}
            <strong style={{ color: colors.text }}>{fmtEps(d?.estimatedEps)}</strong>
          </div>
          {(d?.surprise != null || surprisePct) && (
            <div style={{ marginTop: 4 }}>
              Surprise:{' '}
              <strong style={{ color: beatColor }}>
                {d?.surprise != null
                  ? `${d.surprise > 0 ? '+' : ''}${Number(d.surprise).toFixed(2)}`
                  : '—'}
                {surprisePct ? ` (${surprisePct})` : ''}
                {beat ? ` · ${beat}` : ''}
              </strong>
            </div>
          )}
          {d?.reportedDate && (
            <div style={{ marginTop: 6, color: colors.textMuted, fontSize: 11 }}>
              Reported {d.reportedDate}
              {d.reportTime ? ` · ${d.reportTime}` : ''}
            </div>
          )}
        </>
      ) : (
        <div>
          EPS:{' '}
          <strong style={{ color: d?.eps >= 0 ? colors.success : colors.danger }}>
            {fmtEps(d?.eps)}
          </strong>
        </div>
      )}
      {d?.revenue > 0 && (
        <div style={{ marginTop: 4, color: colors.textMuted }}>
          Revenue:{' '}
          <strong style={{ color: colors.text }}>
            {d.revenue >= 1e9
              ? `$${(d.revenue / 1e9).toFixed(2)}B`
              : `$${(d.revenue / 1e6).toFixed(0)}M`}
          </strong>
        </div>
      )}
    </div>
  );
};

// ─── EPS Chart ───────────────────────────────────────────────────────────────

const EpsChart = ({ quartersHistory, yearsHistory, colors, isMobile }) => {
  const [view, setView] = useState('quarterly');
  const isQuarterly = view === 'quarterly';
  const data = (isQuarterly ? quartersHistory : yearsHistory) || [];
  const dataKey = isQuarterly ? 'date' : 'year';
  const isEmpty = data.length === 0;
  const hasEstimates = isQuarterly && data.some((d) => d.estimatedEps != null);

  const toggleStyle = (active) => ({
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    background: active ? colors.primary : 'transparent',
    color: active ? '#fff' : colors.textMuted,
    transition: 'all 0.15s ease',
  });

  const actualFill = (entry) => {
    if (!hasEstimates || entry.surprise == null) {
      return entry.eps > 0 ? colors.success : entry.eps < 0 ? colors.danger : colors.textMuted;
    }
    if (entry.surprise > 0) return colors.success;
    if (entry.surprise < 0) return colors.danger;
    return colors.textMuted;
  };

  const legendItems = isQuarterly && hasEstimates
    ? [
        { color: colors.success, label: 'Beat' },
        { color: colors.danger, label: 'Miss' },
        { color: colors.textMuted, label: 'Estimate' },
      ]
    : [
        { color: colors.success, label: 'Positive' },
        { color: colors.danger, label: 'Negative' },
      ];

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: '18px 16px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>EPS History</span>
        <div style={{ display: 'flex', gap: 2, background: colors.bgSecondary, borderRadius: 7, padding: 2 }}>
          <button type="button" style={toggleStyle(isQuarterly)} onClick={() => setView('quarterly')}>
            Quarterly
          </button>
          <button type="button" style={toggleStyle(!isQuarterly)} onClick={() => setView('annual')}>
            Annual
          </button>
        </div>
      </div>
      {isEmpty ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: isMobile ? 160 : 200,
            gap: 8,
            color: colors.textMuted,
          }}
        >
          <span style={{ fontSize: 24 }}>📊</span>
          <span style={{ fontSize: 13 }}>Earnings history not available</span>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
            <BarChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }} barGap={2} barCategoryGap="18%">
              <CartesianGrid vertical={false} stroke={colors.border} strokeOpacity={0.5} />
              <XAxis
                dataKey={dataKey}
                tick={{ fill: colors.textMuted, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: colors.textMuted, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <RechartsTooltip
                content={<EpsTooltip colors={colors} isQuarterly={isQuarterly} />}
                cursor={{ fill: `${colors.border}40` }}
              />
              <ReferenceLine y={0} stroke={colors.border} />
              {hasEstimates && (
                <Bar
                  dataKey="estimatedEps"
                  name="Estimate"
                  fill={colors.textMuted}
                  fillOpacity={0.35}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
              )}
              <Bar dataKey="eps" name="Actual" radius={[4, 4, 0, 0]} maxBarSize={hasEstimates ? 28 : 36}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={actualFill(entry)} fillOpacity={0.9} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {legendItems.map(({ color, label }) => (
              <div
                key={label}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: colors.textMuted }}
              >
                <div style={{ width: 8, height: 8, borderRadius: 2, background: color, opacity: label === 'Estimate' ? 0.45 : 1 }} />
                {label}
              </div>
            ))}
          </div>
          {isQuarterly && hasEstimates && (
            <div style={{ marginTop: 14, overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  color: colors.text,
                }}
              >
                <thead>
                  <tr style={{ color: colors.textMuted, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Quarter</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Actual</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Est.</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Surprise</th>
                    {!isMobile && (
                      <th style={{ padding: '6px 8px', fontWeight: 600 }}>Reported</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {[...data].reverse().map((row) => {
                    const pct = fmtSurprisePct(row.surprisePercentage);
                    const surpriseColor =
                      row.surprise > 0
                        ? colors.success
                        : row.surprise < 0
                          ? colors.danger
                          : colors.textMuted;
                    return (
                      <tr key={row.date} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '7px 8px', color: colors.textMuted }}>{row.date}</td>
                        <td style={{ padding: '7px 8px', fontWeight: 600 }}>{fmtEps(row.eps)}</td>
                        <td style={{ padding: '7px 8px' }}>{fmtEps(row.estimatedEps)}</td>
                        <td style={{ padding: '7px 8px', color: surpriseColor, fontWeight: 600 }}>
                          {pct || '—'}
                        </td>
                        {!isMobile && (
                          <td style={{ padding: '7px 8px', color: colors.textMuted }}>
                            {row.reportedDate || '—'}
                            {row.reportTime ? ` · ${row.reportTime}` : ''}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Analyst Consensus Bar ───────────────────────────────────────────────────

// ─── Company Profile Modal ───────────────────────────────────────────────────

const CompanyAbout = ({ profile, colors }) => {
  const [expanded, setExpanded] = useState(false);
  const desc = profile?.description || '';
  if (!desc) return null;
  const short = desc.length > 220 ? `${desc.slice(0, 220)}…` : desc;

  return (
    <p style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.55, margin: '10px 0 0', maxWidth: 640 }}>
      {expanded ? desc : short}
      {desc.length > 220 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          style={{ background: 'none', border: 'none', color: colors.primary, fontSize: 13, cursor: 'pointer', padding: 0, marginLeft: 4 }}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </p>
  );
};

const CompanyDetails = ({ profile, colors }) => {
  const [open, setOpen] = useState(false);

  const sectionLabel = (text, first) => (
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.primary, marginBottom: 8, marginTop: first ? 0 : 20 }}>
      {text}
    </div>
  );

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: `1px solid ${colors.border}` }}>
      <span style={{ fontSize: 12, color: colors.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, color: colors.text, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '12px 14px',
          background: 'none',
          border: 'none',
          color: colors.text,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        Company details
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div style={{ padding: '4px 14px 16px' }}>
          {sectionLabel('Details', true)}
          {row('Sector', profile.sector)}
          {row('Industry', profile.industry)}
          {row('Country', profile.country)}
          {row('Exchange', profile.exchange)}
          {profile.ipoDate && row('IPO Date', fmtDate(profile.ipoDate))}
          {profile.employees && row('Employees', parseInt(profile.employees, 10).toLocaleString())}
          {profile.website && (
            <a
              href={profile.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: colors.primary, marginTop: 10, textDecoration: 'none' }}
            >
              {profile.website.replace(/^https?:\/\//, '')}
              <ArrowUpRight size={13} />
            </a>
          )}

          {sectionLabel('Shares')}
          {row('Shares Outstanding', fmtShares(profile.sharesOutstanding))}
          {row('Market Cap', fmtLargeNum(profile.mktCap))}

          {sectionLabel('Dividends')}
          {row('Dividend Yield', profile.dividendYield ? fmtPct(profile.dividendYield) : '—')}
        </div>
      )}
    </div>
  );
};

// ─── Research Mini Chart moved to src/components/shared/MiniStockChart.js ────

// ─── Ticker search (dashboard embed) ─────────────────────────────────────────

const ResearchTickerSearch = ({ symbol, onSearch, colors, isMobile, inline = false }) => {
  const [val, setVal] = useState(symbol || '');

  useEffect(() => {
    setVal(symbol || '');
  }, [symbol]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const sym = val.trim().toUpperCase();
    if (sym) onSearch(sym);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'nowrap',
        flex: inline ? (isMobile ? '1 1 auto' : '0 1 auto') : undefined,
        minWidth: inline ? 0 : undefined,
        marginLeft: inline ? 'auto' : undefined,
      }}
    >
      <input
        value={val}
        onChange={(e) => setVal(e.target.value.toUpperCase())}
        placeholder="Ticker symbol"
        aria-label="Ticker symbol"
        style={{
          background: colors.bgCard || colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          color: colors.text,
          padding: inline ? '6px 12px' : (isMobile ? '8px 12px' : '8px 14px'),
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '0.04em',
          outline: 'none',
          width: inline && isMobile ? '100%' : (inline ? 140 : (isMobile ? '100%' : 140)),
          maxWidth: inline && isMobile ? 160 : '100%',
          minWidth: 0,
          flex: inline && isMobile ? '1 1 72px' : '0 1 auto',
        }}
      />
      <button
        type="submit"
        aria-label="Search"
        title="Search"
        style={{
          width: 34,
          height: 34,
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          borderRadius: 8,
          border: 'none',
          background: colors.primary,
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        <Search size={16} aria-hidden />
      </button>
    </form>
  );
};

// ─── Section wrapper ─────────────────────────────────────────────────────────

const SectionLabel = ({ children, colors }) => (
  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, marginBottom: 10 }}>
    {children}
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

const CompanyFundamentals = ({ hideHeader = false }) => {
  const { symbol: routeSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const querySymbol = (searchParams.get('symbol') || '').trim().toUpperCase();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  const [activeSymbol, setActiveSymbol] = useState(
    hideHeader ? querySymbol : (routeSymbol?.toUpperCase() || '')
  );

  const { company, isLoading, isError, error, companyLoading } = useCompanyFundamentals(activeSymbol);

  useEffect(() => {
    if (hideHeader) {
      setActiveSymbol(querySymbol);
    } else if (routeSymbol) {
      setActiveSymbol(routeSymbol.toUpperCase());
    }
  }, [routeSymbol, hideHeader, querySymbol]);

  const handleSymbolSearch = useCallback((sym) => {
    if (!sym) return;
    navigate(dashboardResearchPath(sym.toUpperCase()));
  }, [navigate]);

  const pageStyle = hideHeader
    ? { minHeight: 0, color: colors.text, paddingBottom: 24 }
    : {
        minHeight: '100dvh',
        backgroundColor: colors.bgPrimary,
        color: colors.text,
        paddingBottom: 48,
      };

  const containerStyle = {
    maxWidth: 860,
    margin: '0 auto',
    padding: hideHeader
      ? (isMobile ? '0 0 16px' : '0 0 24px')
      : (isMobile ? '16px 12px' : '24px 24px'),
  };

  const embedHeader = hideHeader ? (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 12,
          marginBottom: 8,
          minWidth: 0,
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text, margin: 0, flexShrink: 0 }}>
          Research
        </h1>
        <ResearchTickerSearch
          symbol={activeSymbol}
          onSearch={handleSymbolSearch}
          colors={colors}
          isMobile={isMobile}
          inline
        />
      </div>
      <p style={{ color: colors.textMuted, fontSize: 14, margin: 0 }}>
        Company fundamentals, valuation, and earnings history.
      </p>
    </div>
  ) : null;

  const shimmerCSS = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;

  // ── Empty / initial state ──
  if (!activeSymbol) {
    return (
      <div style={pageStyle}>
        <style>{shimmerCSS}</style>
        {!hideHeader && <Header />}
        <div style={{ ...containerStyle, paddingTop: hideHeader ? 0 : 60, textAlign: hideHeader ? 'left' : 'center' }}>
          {embedHeader}
          {!hideHeader && (
            <>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📈</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Company Research</h2>
              <p style={{ color: colors.textMuted, fontSize: 14 }}>
                Use the search in the header to look up a ticker symbol.
              </p>
            </>
          )}
          {hideHeader && (
            <>
              <div style={{ marginBottom: 20 }}>
                <p style={{ color: colors.textMuted, fontSize: 13, margin: '0 0 10px' }}>
                  Search a ticker above, or try one of these:
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['AAPL', 'MSFT', 'TSLA', 'NVDA', 'AMZN', 'SPY'].map((sym) => (
                    <button
                      key={sym}
                      type="button"
                      onClick={() => handleSymbolSearch(sym)}
                      style={{
                        padding: '4px 14px',
                        borderRadius: 6,
                        border: `1px solid ${colors.border}`,
                        background: colors.bgCard,
                        color: colors.text,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        letterSpacing: '0.02em',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = colors.primary;
                        e.currentTarget.style.background = `${colors.primary}14`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.background = colors.bgCard;
                      }}
                    >
                      {sym}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ghost KPI cards */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: 10,
                  marginBottom: 16,
                  opacity: 0.35,
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {['Market Cap', 'Revenue TTM', 'Net Margin', 'EPS (TTM)'].map((label) => (
                  <div
                    key={label}
                    style={{
                      background: colors.bgCard,
                      border: `1px dashed ${colors.border}`,
                      borderRadius: 12,
                      padding: '16px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {label}
                    </span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: colors.text, lineHeight: 1.1, marginTop: 2 }}>
                      —
                    </span>
                  </div>
                ))}
              </div>

              {/* Ghost chart area */}
              <div
                style={{
                  background: colors.bgCard,
                  border: `1px dashed ${colors.border}`,
                  borderRadius: 12,
                  height: isMobile ? 160 : 220,
                  marginBottom: 16,
                  opacity: 0.35,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ color: colors.textMuted, fontSize: 13 }}>Price Chart</span>
              </div>

              {/* Ghost profitability strip */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', opacity: 0.35, pointerEvents: 'none', userSelect: 'none', marginBottom: 16 }}>
                {['Gross Margin', 'Op Margin', 'ROE', 'ROA', 'EBITDA'].map((label) => (
                  <div
                    key={label}
                    style={{
                      background: colors.bgSecondary,
                      border: `1px dashed ${colors.border}`,
                      borderRadius: 8,
                      padding: '6px 12px',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 500 }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>—</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <style>{shimmerCSS}</style>
      {!hideHeader && <Header />}

      <div style={containerStyle}>
        {embedHeader}

        {/* ── Unknown ticker ── */}
        {isError && !isLoading && error?.notFound && (
          <div
            style={{
              background: colors.bgCard,
              border: `1px dashed ${colors.border}`,
              borderRadius: 12,
              padding: isMobile ? '28px 18px' : '36px 24px',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>Whoops</div>
            <p style={{ margin: 0, color: colors.text, fontSize: 16, fontWeight: 650 }}>
              We couldn’t find {activeSymbol}.
            </p>
            <p style={{ margin: '8px 0 0', color: colors.textMuted, fontSize: 14 }}>
              Double-check the ticker and try another search.
            </p>
          </div>
        )}

        {/* ── Other load errors ── */}
        {isError && !isLoading && !error?.notFound && (
          <div
            style={{
              background: `${colors.danger}18`,
              border: `1px solid ${colors.danger}40`,
              borderRadius: 10,
              padding: '16px 18px',
              color: colors.danger,
              fontSize: 14,
              marginBottom: 20,
            }}
          >
            {error?.message || 'Could not load data for this ticker. Check the symbol and try again.'}
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Skeleton height={32} width="50%" colors={colors} />
            <Skeleton height={16} width="35%" colors={colors} />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginTop: 8 }}>
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} height={88} radius={12} colors={colors} />)}
            </div>
            <Skeleton height={48} radius={8} colors={colors} />
            <Skeleton height={isMobile ? 260 : 320} radius={12} colors={colors} />
            <Skeleton height={48} radius={8} colors={colors} />
            <Skeleton height={100} radius={12} colors={colors} />
          </div>
        )}

        {/* ── Main content ── */}
        {company && !companyLoading && (
          <>
            {/* ── Company Header ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, margin: 0, color: colors.text, lineHeight: 1.1 }}>
                  {company.symbol}
                </h1>
                <span style={{ fontSize: isMobile ? 14 : 17, color: colors.textMuted, fontWeight: 400, alignSelf: 'center' }}>
                  {company.companyName}
                </span>
              </div>
              <CompanyAbout profile={company} colors={colors} />
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {[company.exchange, company.sector, company.industry].filter(Boolean).map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: `${colors.primary}18`, border: `1px solid ${colors.primary}30`,
                      color: colors.primary, fontSize: 11, fontWeight: 500, borderRadius: 5, padding: '2px 8px',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Mini Chart ── */}
            <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
              <MiniStockChart symbol={activeSymbol} colors={colors} height={isMobile ? 200 : 260} />
              {company.week52Low != null && company.week52High != null && (
                <div style={{ borderTop: `1px solid ${colors.border}`, padding: '12px 14px 14px' }}>
                  {(() => {
                    const lo = company.week52Low;
                    const hi = company.week52High;
                    const span = hi - lo;
                    return (
                      <>
                        <div style={{ height: 6, borderRadius: 3, background: `linear-gradient(to right, ${colors.danger}, ${colors.textMuted}, ${colors.success})`, marginBottom: 8 }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.textMuted }}>
                          <span style={{ color: colors.danger }}>${lo.toFixed(2)}</span>
                          <span>52-week range</span>
                          <span style={{ color: colors.success }}>${hi.toFixed(2)}</span>
                        </div>
                        {span > 0 && (
                          <div style={{ marginTop: 6, fontSize: 12, color: colors.textMuted }}>
                            Width: <strong style={{ color: colors.text }}>${span.toFixed(2)}</strong>
                            {' · '}
                            From low: <strong style={{ color: colors.text }}>${(hi - lo).toFixed(2)} ({((span / lo) * 100).toFixed(1)}%)</strong>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <CompanyDetails profile={company} colors={colors} />

            {/* ── KPI Grid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              <KpiCard label="Market Cap"   value={fmtLargeNum(company.mktCap)} colors={colors} />
              <KpiCard
                label="Revenue TTM"
                value={fmtLargeNum(company.revenueTTM)}
                sub={company.revenueGrowthYoY != null
                  ? `${company.revenueGrowthYoY >= 0 ? '+' : ''}${(company.revenueGrowthYoY * 100).toFixed(1)}% YoY`
                  : undefined}
                accentColor={company.revenueGrowthYoY != null && company.revenueGrowthYoY < 0 ? colors.danger : undefined}
                colors={colors}
              />
              <KpiCard
                label="Net Margin"
                value={fmtPct(company.netMarginTTM)}
                accentColor={company.netMarginTTM != null && company.netMarginTTM >= 0.1 ? colors.success : colors.text}
                colors={colors}
              />
              <KpiCard label="EPS (TTM)" value={company.epsTTM != null ? `$${company.epsTTM.toFixed(2)}` : '—'} colors={colors} />
            </div>

            {/* ── Profitability Strip ── */}
            <div style={{ marginBottom: 16 }}>
              <SectionLabel colors={colors}>Profitability</SectionLabel>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatPill label="Gross Margin" value={fmtPct(company.grossMarginTTM)} colors={colors} />
                <StatPill label="Op Margin"    value={fmtPct(company.opMarginTTM)} colors={colors} />
                <StatPill label="ROE"          value={fmtPct(company.roe)} colors={colors} />
                <StatPill label="ROA"          value={fmtPct(company.roa)} colors={colors} />
              </div>
            </div>

            {/* ── Ownership & growth ── */}
            {(company.percentInsiders != null
              || company.percentInstitutions != null
              || company.revenueGrowthYoY != null
              || company.earningsGrowthYoY != null) && (
              <div style={{ marginBottom: 16 }}>
                <SectionLabel colors={colors}>Ownership & Growth</SectionLabel>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {company.percentInsiders != null && (
                    <StatPill label="Insider %" value={fmtPercentDirect(company.percentInsiders)} colors={colors} />
                  )}
                  {company.percentInstitutions != null && (
                    <StatPill label="Institutional %" value={fmtPercentDirect(company.percentInstitutions)} colors={colors} />
                  )}
                  {company.revenueGrowthYoY != null && (
                    <StatPill label="Rev Growth YoY" value={fmtGrowthYoY(company.revenueGrowthYoY)} colors={colors} />
                  )}
                  {company.earningsGrowthYoY != null && (
                    <StatPill label="EPS Growth YoY" value={fmtGrowthYoY(company.earningsGrowthYoY)} colors={colors} />
                  )}
                </div>
              </div>
            )}

            {/* ── Valuation ── */}
            <div style={{ marginBottom: 16 }}>
              <SectionLabel colors={colors}>Valuation</SectionLabel>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatPill label="P/E"       value={fmtNum(company.pe, '', 'x')} colors={colors} />
                {company.fwdPe != null && <StatPill label="Fwd P/E" value={fmtNum(company.fwdPe, '', 'x')} colors={colors} />}
                <StatPill label="P/B"       value={fmtNum(company.pb, '', 'x')} colors={colors} />
                <StatPill label="EV/EBITDA" value={fmtNum(company.evEbitda, '', 'x')} colors={colors} />
                <StatPill label="P/S"       value={fmtNum(company.ps, '', 'x')} colors={colors} />
                <StatPill label="Beta"      value={fmtNum(company.beta)} colors={colors} />
                <StatPill label="Div Yield" value={company.dividendYield ? fmtPct(company.dividendYield) : '—'} colors={colors} />
              </div>
            </div>

            {/* ── EPS Chart ── */}
            <div style={{ marginBottom: 16 }}>
              <SectionLabel colors={colors}>Earnings Per Share</SectionLabel>
              <EpsChart
                quartersHistory={company.quartersHistory}
                yearsHistory={company.yearsHistory}
                colors={colors}
                isMobile={isMobile}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyFundamentals;
